import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, computed, effect, inject, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, forkJoin, map, of } from 'rxjs';
import type { TestimonialsListingPageData } from '../../../shared/models/marketing.models';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { SuccessStoriesGridComponent } from '../../components/success-stories/success-stories-grid.component';
import { SuccessStoriesHeroComponent } from '../../components/success-stories/success-stories-hero.component';
import { SuccessStoriesViewAllComponent } from '../../components/success-stories/success-stories-view-all.component';
import {
  heroStatsFromHome,
  LANDING_STORIES_LIMIT,
} from '../../components/success-stories/success-stories.utils';
import {
  mapTestimonialCards,
  TESTIMONIALS_SECTION_DEFAULTS,
} from '../../components/testimonials/testimonials.utils';
import { buildPageTitle } from '../../../shared/constants/site-meta';
import { PagesService } from '../../../shared/services/pages.service';

export type TestimonialsPageLoadState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | { kind: 'ok'; page: TestimonialsListingPageData; homeStats: Array<{ value: string; label: string }> };

@Component({
  selector: 'app-marketing-testimonials-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingFooterComponent,
    SuccessStoriesHeroComponent,
    SuccessStoriesGridComponent,
    SuccessStoriesViewAllComponent,
    MarketingConsultationCtaComponent,
  ],
  templateUrl: './testimonials-page.component.html',
})
export class MarketingTestimonialsPageComponent {
  private readonly pages = inject(PagesService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);

  readonly landingLimit = LANDING_STORIES_LIMIT;

  readonly pageState = toSignal(
    forkJoin({
      page: this.pages.getMarketingTestimonialsListing(),
      home: this.pages.getMarketingHome().pipe(catchError(() => of(null))),
    }).pipe(
      map(({ page, home }) => ({
        kind: 'ok' as const,
        page,
        homeStats: home?.stats ?? [],
      })),
      catchError(() =>
        isPlatformServer(this.platformId)
          ? of({ kind: 'pending' as const })
          : of({ kind: 'error' as const }),
      ),
    ),
    { initialValue: { kind: 'pending' as const } satisfies TestimonialsPageLoadState },
  );

  readonly allTestimonials = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return [];
    return mapTestimonialCards(state.page.items);
  });

  readonly featuredTestimonials = computed(() => this.allTestimonials().slice(0, LANDING_STORIES_LIMIT));

  readonly totalCount = computed(() => this.allTestimonials().length);

  readonly hasAnyTestimonials = computed(() => this.totalCount() > 0);

  readonly heroStats = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return heroStatsFromHome();
    return heroStatsFromHome(state.homeStats);
  });

  constructor() {
    effect(() => {
      const state = this.pageState();
      if (state.kind === 'ok') {
        this.applySeo();
      }
    });
  }

  private applySeo(): void {
    const state = this.pageState();
    if (state.kind !== 'ok') return;
    const pageTitle = state.page.title?.trim() || TESTIMONIALS_SECTION_DEFAULTS.title;
    const pageSubtitle = state.page.subtitle?.trim() || TESTIMONIALS_SECTION_DEFAULTS.subtitle;
    this.title.setTitle(buildPageTitle(pageTitle));
    this.meta.updateTag({ name: 'description', content: pageSubtitle });
  }
}
